import { c as _c } from "react/compiler-runtime";
import { Card, CardContent, Divider, Stack, styled, Switch, Tooltip, Typography, type Theme } from '@mui/material';
import type { Condition, BackupPolicyConfigResponse } from '@repo/api-gw-sdk';
import { BackupPolicyType, BackupStatus, ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { createScopeCondition } from '@/components/backupStatus/configurationStatusScope';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { ConfirmationDialog } from '@/components/dialogs/confirmationDialog';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { backupPolicies } from '@/components/queryBuilder/properties';
import { getResourcesScopeType, type ResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { ResourcesSelectionViewer } from '@/components/resourcesSelection/resourcesSelectionViewer';
import { NavigationLink } from '@/components/shared/link';
import { Loader } from '@/components/shared/loader';
import { TagsElasticContainer } from '@/components/tags/tagsElasticContainer';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { AccountAsVaultTag, VaultTag } from '@/components/vaults/vaultTag';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import type { BackupVault } from '@/data/vaults/backupVault';
import { createCombineCondition } from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';
import { BackupPolicyConditionTitle } from './BackupPolicyConditionEditor';
import { EntraIdResourceSelectionViewer } from './EntraIdResourceSelectionViewer';
import { GwsResourceSelectionViewer } from './GwsResourceSelectionViewer';
import { M365ResourceSelectionViewer } from './M365ResourceSelectionViewer';
import { backupPolicyNativeProvider, isAzureEntraIdPolicyType, isContinuousPolicyType, isGoogleWorkspacePolicyType, isMicrosoft365PolicyType, isNativePolicyType, isPitrPolicyType, isSingleTenantSaasPolicyType } from './backupPoliciesTypes';
import { useAllBackupPolicyProperties } from './backupPolicyProperties';
import { BackupPolicyClassificationSchedule, BackupPolicyContinuousSchedulesTable, BackupPolicyScheduleTimezoneNote, BackupPolicySchedulesTable } from './backupPolicySchedulesTable';
import { BackupPolicyTypeTag } from './backupPolicyTypeTag';
import { BackupPolicyTypeDescription } from './customPolicySupportedDisclaimer';
export const BPCard = styled(Card)((props: {
  disabled: boolean;
  theme?: Theme;
  style?: object;
}) => {
  return {
    border: '1px solid var(--mui-palette-divider)',
    backgroundColor: props.disabled ? 'var(--mui-palette-background-default)' : 'var(--mui-palette-background-paper)',
    ...props.theme?.typography.body2,
    textAlign: 'left',
    color: props.theme?.palette.text.secondary,
    minWidth: '550px',
    minHeight: '170px',
    boxShadow: 'none',
    paddingTop: '12px',
    borderRadius: '16px',
    '& .MuiCardContent-root': {
      padding: '0px 24px 24px'
    },
    ...props.style
  };
});
interface BackupPolicyCardProps {
  processing?: boolean;
  policy: BackupPolicyConfigResponse;
  vaults?: BackupVault[];
  defaultExpanded?: boolean;
  onEdit?: (policy: BackupPolicyConfigResponse) => void;
  onDelete?: (policy: BackupPolicyConfigResponse) => void;
  onDuplicate?: (policy: BackupPolicyConfigResponse) => void;
  onEnabledPolicyUpdate?: (policy: BackupPolicyConfigResponse) => Promise<boolean>;
}
export function BackupPolicyCard(props) {
  const $ = _c(195);
  const enabled = props.policy.enabled;
  const {
    snackbar
  } = useWorkspace();
  const {
    isAuthorized
  } = useRoles();
  const {
    InventoryStatusColumns,
    PolicyAndVaultMetadataLabels: t0
  } = useFeatureFlags();
  const PolicyAndVaultMetadataLabels = t0 === undefined ? false : t0;
  const allBackupPolicyProperties = useAllBackupPolicyProperties();
  const [vaultsSectionExpanded, setVaultsSectionExpanded] = useState(!!props.defaultExpanded);
  const [disablePolicyDialogOpen, setDisablePolicyDialogOpen] = useState(false);
  const hasClassificationSchedule = !!props.policy.classificationSchedule;
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let T5;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let togglePolicyEnabledState;
  if ($[0] !== InventoryStatusColumns || $[1] !== PolicyAndVaultMetadataLabels || $[2] !== allBackupPolicyProperties || $[3] !== enabled || $[4] !== isAuthorized || $[5] !== props || $[6] !== snackbar || $[7] !== vaultsSectionExpanded) {
    const matchingVaults = props.vaults?.filter(vault => props.policy?.schedules.find(schedule => schedule.vaultId === vault.id)).slice(0, 2);
    let t17;
    if ($[31] !== props.policy.condition) {
      t17 = getResourcesScopeType(props.policy.condition, "allIncluded");
      $[31] = props.policy.condition;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    const resourcesScopeType = t17;
    let t18;
    if ($[33] !== props.policy.policyType) {
      t18 = isSingleTenantSaasPolicyType(props.policy.policyType);
      $[33] = props.policy.policyType;
      $[34] = t18;
    } else {
      t18 = $[34];
    }
    const isSaasWorkspace = t18;
    let t19;
    if ($[35] !== props || $[36] !== snackbar) {
      t19 = async () => {
        const enabled_0 = props.policy.enabled;
        const updated = await props.onEnabledPolicyUpdate?.(props.policy);
        if (updated === false) {
          return;
        }
        snackbar.showMessage(<><span className="font-semibold">{props.policy.name}</span> backup policy is now {enabled_0 ? "disabled" : "enabled"}.</>);
      };
      $[35] = props;
      $[36] = snackbar;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    togglePolicyEnabledState = t19;
    let t20;
    if ($[38] !== enabled || $[39] !== togglePolicyEnabledState) {
      t20 = () => {
        if (enabled) {
          setDisablePolicyDialogOpen(true);
        } else {
          togglePolicyEnabledState();
        }
      };
      $[38] = enabled;
      $[39] = togglePolicyEnabledState;
      $[40] = t20;
    } else {
      t20 = $[40];
    }
    const onToggleClicked = t20;
    const t21 = props.policy?.id || "";
    let T6;
    let T7;
    let t22;
    let t23;
    let t24;
    let t25;
    let t26;
    let t27;
    if ($[41] !== InventoryStatusColumns || $[42] !== enabled || $[43] !== isAuthorized || $[44] !== isSaasWorkspace || $[45] !== props.onDelete || $[46] !== props.onDuplicate || $[47] !== props.onEdit || $[48] !== props.onEnabledPolicyUpdate || $[49] !== props.policy.attachedResources || $[50] !== props.policy.name || $[51] !== props.policy.policyType || $[52] !== props.processing || $[53] !== t21) {
      const inventoryFilterCondition = {
        operator: ConditionOperatorEnum.ContainsAnyOf,
        value: [t21],
        property: backupPolicies.name
      };
      T5 = BPCard;
      t15 = !enabled;
      t16 = "backup-policy-card";
      T7 = Stack;
      t25 = "row";
      if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
        t26 = {
          maxWidth: "100%",
          justifyContent: "space-between",
          gap: "12px",
          alignItems: "center",
          margin: "12px 24px"
        };
        $[65] = t26;
      } else {
        t26 = $[65];
      }
      let t28;
      if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
        t28 = {
          alignItems: "center",
          gap: "12px",
          minWidth: "0px",
          flexShrink: 1
        };
        $[66] = t28;
      } else {
        t28 = $[66];
      }
      let t29;
      if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
        t29 = {
          minWidth: "0px",
          flexShrink: 1
        };
        $[67] = t29;
      } else {
        t29 = $[67];
      }
      let t30;
      if ($[68] !== props.policy.name) {
        t30 = <Stack sx={t29}><OverflowTooltip text={props.policy.name} renderContent={_temp} /></Stack>;
        $[68] = props.policy.name;
        $[69] = t30;
      } else {
        t30 = $[69];
      }
      let t31;
      if ($[70] !== props.policy.policyType) {
        t31 = <BackupPolicyTypeTag policyType={props.policy.policyType} />;
        $[70] = props.policy.policyType;
        $[71] = t31;
      } else {
        t31 = $[71];
      }
      if ($[72] !== t30 || $[73] !== t31) {
        t27 = <Stack direction="row" sx={t28}>{t30}{t31}</Stack>;
        $[72] = t30;
        $[73] = t31;
        $[74] = t27;
      } else {
        t27 = $[74];
      }
      T6 = Stack;
      t22 = "row";
      if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
        t23 = {
          alignItems: "center",
          gap: "16px"
        };
        $[75] = t23;
      } else {
        t23 = $[75];
      }
      t24 = enabled && !isSaasWorkspace && <>{props.processing ? <Loader size="medium" /> : <NavigationLink path="/inventory" searchParams={{
          query: createCombineCondition([inventoryFilterCondition, createScopeCondition("backupPolicyResources", [BackupStatus.ExcludedFromBackup, BackupStatus.Disconnected, BackupStatus.AccessLost, BackupStatus.Unsupported, BackupStatus.ClassificationFailed, BackupStatus.LimitExceeded], InventoryStatusColumns)])
        }} disabled={!isAuthorized("read:inventory")} className="text-(--mui-palette-primary-main)"><ButtonWithIcon text={`${props.policy.attachedResources ?? 0} ${pluralize("Resource", props.policy.attachedResources ?? 0)}`} iconPosition="right" icon="material-symbols-arrow-right-alt" /></NavigationLink>}{(props.onEdit && props.onDelete && props.onDuplicate || props.onEnabledPolicyUpdate) && <Divider sx={{
          height: "16px"
        }} orientation="vertical" />}</>;
      $[41] = InventoryStatusColumns;
      $[42] = enabled;
      $[43] = isAuthorized;
      $[44] = isSaasWorkspace;
      $[45] = props.onDelete;
      $[46] = props.onDuplicate;
      $[47] = props.onEdit;
      $[48] = props.onEnabledPolicyUpdate;
      $[49] = props.policy.attachedResources;
      $[50] = props.policy.name;
      $[51] = props.policy.policyType;
      $[52] = props.processing;
      $[53] = t21;
      $[54] = T5;
      $[55] = T6;
      $[56] = T7;
      $[57] = t15;
      $[58] = t16;
      $[59] = t22;
      $[60] = t23;
      $[61] = t24;
      $[62] = t25;
      $[63] = t26;
      $[64] = t27;
    } else {
      T5 = $[54];
      T6 = $[55];
      T7 = $[56];
      t15 = $[57];
      t16 = $[58];
      t22 = $[59];
      t23 = $[60];
      t24 = $[61];
      t25 = $[62];
      t26 = $[63];
      t27 = $[64];
    }
    let t28;
    if ($[76] !== isAuthorized || $[77] !== isSaasWorkspace || $[78] !== props) {
      t28 = (isSaasWorkspace && props.onDelete || props.onEdit && props.onDelete && props.onDuplicate) && <OptionMenu options={[...(isSaasWorkspace ? [{
        id: "edit-policy",
        text: "Edit",
        menuItemProps: {
          disabled: !isAuthorized("update:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onEdit?.(props.policy);
          }
        }
      }, {
        id: "divider",
        divider: true
      }] : [{
        id: "edit-policy",
        text: "Edit",
        menuItemProps: {
          disabled: !isAuthorized("update:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onEdit?.(props.policy);
          }
        }
      }, {
        id: "duplicate-policy",
        text: "Duplicate",
        menuItemProps: {
          disabled: !isAuthorized("create:policies"),
          onClick: () => {
            props.onDuplicate?.(props.policy);
          }
        }
      }, {
        id: "divider",
        divider: true
      }]), {
        id: "delete-policy",
        text: "Delete",
        menuItemProps: {
          disabled: !isAuthorized("delete:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onDelete?.(props.policy);
          }
        }
      }]} />;
      $[76] = isAuthorized;
      $[77] = isSaasWorkspace;
      $[78] = props;
      $[79] = t28;
    } else {
      t28 = $[79];
    }
    let t29;
    if ($[80] !== enabled || $[81] !== isAuthorized || $[82] !== onToggleClicked || $[83] !== props.onEnabledPolicyUpdate) {
      t29 = props.onEnabledPolicyUpdate && <Tooltip title={enabled ? "This policy is enabled" : "This policy is disabled"}><Switch disabled={!isAuthorized("update:policies")} checked={enabled} onChange={onToggleClicked} /></Tooltip>;
      $[80] = enabled;
      $[81] = isAuthorized;
      $[82] = onToggleClicked;
      $[83] = props.onEnabledPolicyUpdate;
      $[84] = t29;
    } else {
      t29 = $[84];
    }
    let t30;
    if ($[85] !== T6 || $[86] !== t22 || $[87] !== t23 || $[88] !== t24 || $[89] !== t28 || $[90] !== t29) {
      t30 = <T6 direction={t22} sx={t23}>{t24}{t28}{t29}</T6>;
      $[85] = T6;
      $[86] = t22;
      $[87] = t23;
      $[88] = t24;
      $[89] = t28;
      $[90] = t29;
      $[91] = t30;
    } else {
      t30 = $[91];
    }
    if ($[92] !== T7 || $[93] !== t25 || $[94] !== t26 || $[95] !== t27 || $[96] !== t30) {
      t13 = <T7 direction={t25} sx={t26}>{t27}{t30}</T7>;
      $[92] = T7;
      $[93] = t25;
      $[94] = t26;
      $[95] = t27;
      $[96] = t30;
      $[97] = t13;
    } else {
      t13 = $[97];
    }
    if ($[98] !== PolicyAndVaultMetadataLabels || $[99] !== props.policy.tags) {
      t14 = PolicyAndVaultMetadataLabels && !!Object.keys(props.policy.tags ?? {}).length && <Stack className="px-[24px] pt-[12px]"><TagsElasticContainer tags={props.policy.tags ?? {}} expandable={true} /></Stack>;
      $[98] = PolicyAndVaultMetadataLabels;
      $[99] = props.policy.tags;
      $[100] = t14;
    } else {
      t14 = $[100];
    }
    T4 = CardContent;
    if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = {
        marginTop: "24px",
        "&&": {
          padding: "0 0 24px"
        }
      };
      $[101] = t12;
    } else {
      t12 = $[101];
    }
    T3 = Stack;
    if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <Divider />;
      t10 = {
        gap: "12px"
      };
      $[102] = t10;
      $[103] = t9;
    } else {
      t10 = $[102];
      t9 = $[103];
    }
    let t31;
    if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = {
        px: "24px"
      };
      $[104] = t31;
    } else {
      t31 = $[104];
    }
    let t32;
    if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = <AccordionSummary><Typography variant="h4">{"Resource selection"}</Typography></AccordionSummary>;
      $[105] = t32;
    } else {
      t32 = $[105];
    }
    let t33;
    if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = {
        gap: "24px"
      };
      $[106] = t33;
    } else {
      t33 = $[106];
    }
    let t34;
    if ($[107] !== allBackupPolicyProperties || $[108] !== props.policy.condition || $[109] !== props.policy.exclude || $[110] !== props.policy.include || $[111] !== props.policy.policyType || $[112] !== props.policy.resourceTypes || $[113] !== resourcesScopeType) {
      t34 = isGoogleWorkspacePolicyType(props.policy.policyType) ? <GwsResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : isMicrosoft365PolicyType(props.policy.policyType) ? <M365ResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : isAzureEntraIdPolicyType(props.policy.policyType) ? <EntraIdResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : <ResourcesSelectionViewer condition={props.policy.condition} conditionTitle={<BackupPolicyConditionTitle resourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType ?? BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD} />} include={props.policy.include ?? []} exclude={props.policy.exclude ?? []} resourcesScopeType={resourcesScopeType} dynamicResourcesHeader={null} allExcludedDescription={"This backup policy won\u2019t be automatically applied to any resources. Only manually included resources will be assigned to the policy."} allIncludedDescription={isPitrPolicyType(props.policy.policyType) ? "This backup policy will be automatically applied to all resources of the selected type. When new ones are discovered, they\u2019ll be included too." : "This backup policy will be automatically applied to all resources. When new ones are discovered, they\u2019ll be included too."} supportProperties={allBackupPolicyProperties.flat()} selectedResourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType} />;
      $[107] = allBackupPolicyProperties;
      $[108] = props.policy.condition;
      $[109] = props.policy.exclude;
      $[110] = props.policy.include;
      $[111] = props.policy.policyType;
      $[112] = props.policy.resourceTypes;
      $[113] = resourcesScopeType;
      $[114] = t34;
    } else {
      t34 = $[114];
    }
    let t35;
    if ($[115] !== props.policy.policyType || $[116] !== props.policy.vaultStorageTier) {
      t35 = <BackupPolicyTypeDescription policyType={props.policy.policyType} showForPitr={false} vaultStorageTier={props.policy.vaultStorageTier} />;
      $[115] = props.policy.policyType;
      $[116] = props.policy.vaultStorageTier;
      $[117] = t35;
    } else {
      t35 = $[117];
    }
    let t36;
    if ($[118] !== t34 || $[119] !== t35) {
      t36 = <AccordionDetails><Stack sx={t33}>{t34}{t35}</Stack></AccordionDetails>;
      $[118] = t34;
      $[119] = t35;
      $[120] = t36;
    } else {
      t36 = $[120];
    }
    if ($[121] !== props.defaultExpanded || $[122] !== t36) {
      t11 = <Accordion defaultExpanded={props.defaultExpanded} sx={t31}>{t32}{t36}</Accordion>;
      $[121] = props.defaultExpanded;
      $[122] = t36;
      $[123] = t11;
    } else {
      t11 = $[123];
    }
    T2 = Accordion;
    t6 = vaultsSectionExpanded;
    if ($[124] !== vaultsSectionExpanded) {
      t7 = () => setVaultsSectionExpanded(!vaultsSectionExpanded);
      $[124] = vaultsSectionExpanded;
      $[125] = t7;
    } else {
      t7 = $[125];
    }
    if ($[126] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        px: "24px"
      };
      $[126] = t8;
    } else {
      t8 = $[126];
    }
    T1 = AccordionSummary;
    T0 = Stack;
    t1 = "row";
    t2 = "w-full";
    if ($[127] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        justifyContent: "space-between",
        alignItems: "center"
      };
      t4 = <Typography variant="h4">Schedules</Typography>;
      $[127] = t3;
      $[128] = t4;
    } else {
      t3 = $[127];
      t4 = $[128];
    }
    t5 = !vaultsSectionExpanded && <div>{isNativePolicyType(props.policy.policyType) ? <AccountAsVaultTag cloudProvider={backupPolicyNativeProvider(props.policy)} region={props.policy.schedules?.[0]?.region || "Source region"} /> : !!matchingVaults?.length && matchingVaults.map(_temp2)}</div>;
    $[0] = InventoryStatusColumns;
    $[1] = PolicyAndVaultMetadataLabels;
    $[2] = allBackupPolicyProperties;
    $[3] = enabled;
    $[4] = isAuthorized;
    $[5] = props;
    $[6] = snackbar;
    $[7] = vaultsSectionExpanded;
    $[8] = T0;
    $[9] = T1;
    $[10] = T2;
    $[11] = T3;
    $[12] = T4;
    $[13] = T5;
    $[14] = t1;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
    $[19] = t14;
    $[20] = t15;
    $[21] = t16;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = togglePolicyEnabledState;
  } else {
    T0 = $[8];
    T1 = $[9];
    T2 = $[10];
    T3 = $[11];
    T4 = $[12];
    T5 = $[13];
    t1 = $[14];
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
    t13 = $[18];
    t14 = $[19];
    t15 = $[20];
    t16 = $[21];
    t2 = $[22];
    t3 = $[23];
    t4 = $[24];
    t5 = $[25];
    t6 = $[26];
    t7 = $[27];
    t8 = $[28];
    t9 = $[29];
    togglePolicyEnabledState = $[30];
  }
  let t17;
  if ($[129] !== T0 || $[130] !== t1 || $[131] !== t2 || $[132] !== t3 || $[133] !== t4 || $[134] !== t5) {
    t17 = <T0 direction={t1} className={t2} sx={t3}>{t4}{t5}</T0>;
    $[129] = T0;
    $[130] = t1;
    $[131] = t2;
    $[132] = t3;
    $[133] = t4;
    $[134] = t5;
    $[135] = t17;
  } else {
    t17 = $[135];
  }
  let t18;
  if ($[136] !== T1 || $[137] !== t17) {
    t18 = <T1>{t17}</T1>;
    $[136] = T1;
    $[137] = t17;
    $[138] = t18;
  } else {
    t18 = $[138];
  }
  let t19;
  if ($[139] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      paddingRight: 0,
      paddingTop: "24px"
    };
    $[139] = t19;
  } else {
    t19 = $[139];
  }
  let t20;
  if ($[140] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      gap: "24px"
    };
    $[140] = t20;
  } else {
    t20 = $[140];
  }
  let t21;
  if ($[141] !== props.policy) {
    t21 = <BackupPolicyScheduleTimezoneNote policy={props.policy} />;
    $[141] = props.policy;
    $[142] = t21;
  } else {
    t21 = $[142];
  }
  let t22;
  if ($[143] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      gap: "24px"
    };
    $[143] = t22;
  } else {
    t22 = $[143];
  }
  let t23;
  if ($[144] !== hasClassificationSchedule) {
    t23 = hasClassificationSchedule && <Typography variant="body1">Backup schedules</Typography>;
    $[144] = hasClassificationSchedule;
    $[145] = t23;
  } else {
    t23 = $[145];
  }
  let t24;
  if ($[146] !== props.policy || $[147] !== props.vaults) {
    t24 = isContinuousPolicyType(props.policy.policyType) ? <BackupPolicyContinuousSchedulesTable policy={props.policy} /> : <BackupPolicySchedulesTable policy={props.policy} vaults={props.vaults} />;
    $[146] = props.policy;
    $[147] = props.vaults;
    $[148] = t24;
  } else {
    t24 = $[148];
  }
  let t25;
  if ($[149] !== t23 || $[150] !== t24) {
    t25 = <Stack sx={t22}>{t23}{t24}</Stack>;
    $[149] = t23;
    $[150] = t24;
    $[151] = t25;
  } else {
    t25 = $[151];
  }
  let t26;
  if ($[152] !== hasClassificationSchedule || $[153] !== props.policy) {
    t26 = hasClassificationSchedule && <BackupPolicyClassificationSchedule policy={props.policy} />;
    $[152] = hasClassificationSchedule;
    $[153] = props.policy;
    $[154] = t26;
  } else {
    t26 = $[154];
  }
  let t27;
  if ($[155] !== t21 || $[156] !== t25 || $[157] !== t26) {
    t27 = <AccordionDetails sx={t19}><Stack sx={t20}>{t21}{t25}{t26}</Stack></AccordionDetails>;
    $[155] = t21;
    $[156] = t25;
    $[157] = t26;
    $[158] = t27;
  } else {
    t27 = $[158];
  }
  let t28;
  if ($[159] !== T2 || $[160] !== t18 || $[161] !== t27 || $[162] !== t6 || $[163] !== t7 || $[164] !== t8) {
    t28 = <T2 expanded={t6} onChange={t7} sx={t8}>{t18}{t27}</T2>;
    $[159] = T2;
    $[160] = t18;
    $[161] = t27;
    $[162] = t6;
    $[163] = t7;
    $[164] = t8;
    $[165] = t28;
  } else {
    t28 = $[165];
  }
  let t29;
  if ($[166] !== T3 || $[167] !== t10 || $[168] !== t11 || $[169] !== t28 || $[170] !== t9) {
    t29 = <T3 divider={t9} sx={t10}>{t11}{t28}</T3>;
    $[166] = T3;
    $[167] = t10;
    $[168] = t11;
    $[169] = t28;
    $[170] = t9;
    $[171] = t29;
  } else {
    t29 = $[171];
  }
  let t30;
  if ($[172] !== T4 || $[173] !== t12 || $[174] !== t29) {
    t30 = <T4 sx={t12}>{t29}</T4>;
    $[172] = T4;
    $[173] = t12;
    $[174] = t29;
    $[175] = t30;
  } else {
    t30 = $[175];
  }
  let t31;
  if ($[176] !== props.policy.name) {
    t31 = <>The <span className="font-semibold">{props.policy.name}</span>{" "}backup policy will stop being applied to resources. Existing snapshots will be kept until their retention expires.</>;
    $[176] = props.policy.name;
    $[177] = t31;
  } else {
    t31 = $[177];
  }
  let t32;
  if ($[178] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => setDisablePolicyDialogOpen(false);
    $[178] = t32;
  } else {
    t32 = $[178];
  }
  let t33;
  if ($[179] !== togglePolicyEnabledState) {
    t33 = async () => {
      await togglePolicyEnabledState();
      setDisablePolicyDialogOpen(false);
    };
    $[179] = togglePolicyEnabledState;
    $[180] = t33;
  } else {
    t33 = $[180];
  }
  let t34;
  if ($[181] !== disablePolicyDialogOpen || $[182] !== t31 || $[183] !== t33) {
    t34 = <ConfirmationDialog isOpen={disablePolicyDialogOpen} title="Disable backup policy" description={t31} onCancel={t32} onConfirm={t33} buttonTitle="Disable Policy" />;
    $[181] = disablePolicyDialogOpen;
    $[182] = t31;
    $[183] = t33;
    $[184] = t34;
  } else {
    t34 = $[184];
  }
  let t35;
  if ($[185] !== t13 || $[186] !== t14 || $[187] !== t30 || $[188] !== t34) {
    t35 = <>{t13}{t14}{t30}{t34}</>;
    $[185] = t13;
    $[186] = t14;
    $[187] = t30;
    $[188] = t34;
    $[189] = t35;
  } else {
    t35 = $[189];
  }
  let t36;
  if ($[190] !== T5 || $[191] !== t15 || $[192] !== t16 || $[193] !== t35) {
    t36 = <T5 disabled={t15} data-testid={t16}>{t35}</T5>;
    $[190] = T5;
    $[191] = t15;
    $[192] = t16;
    $[193] = t35;
    $[194] = t36;
  } else {
    t36 = $[194];
  }
  return t36;
}
function _temp2(vault_0) {
  return <VaultTag key={vault_0.id} vault={vault_0} />;
}
function _temp(text, ref) {
  return <Typography noWrap={true} variant="h3" ref={ref}>{text}</Typography>;
}